import { useState } from 'react';
import { useSelector } from '@legendapp/state/react';
import { X, Sparkles, Mail, Github, AlertCircle } from 'lucide-react';
import { auth$ } from '@/stores/auth$';
import { linkOAuthIdentity } from '@/stores/auth$';
import AuthModal from './AuthModal';

function GoogleIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none">
      <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
      <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
      <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
      <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
    </svg>
  );
}

/**
 * Banner shown to anonymous users encouraging them to upgrade to a permanent account
 * Shows at the top of the app with options to upgrade via email or OAuth
 */
export default function UpgradeBanner() {
  const isAnonymous = useSelector(() => auth$.isAnonymous.get());
  const [dismissed, setDismissed] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Don't show if not anonymous or if dismissed
  if (!isAnonymous || dismissed) {
    return null;
  }

  const handleOAuthUpgrade = async (provider) => {
    setError('');
    setLoading(true);

    try {
      console.log('[UpgradeBanner] Upgrading to OAuth:', provider);
      await linkOAuthIdentity(provider);
      // OAuth will redirect - banner will disappear after redirect
    } catch (err) {
      console.error('[UpgradeBanner] OAuth upgrade error:', err);
      setError(err.message || 'Failed to upgrade account');
      setLoading(false);
    }
  };

  return (
    <>
      <div className="bg-gradient-to-r from-primary/10 to-primary/5 border-b border-primary/20">
        <div className="max-w-7xl mx-auto px-4 py-3 relative">
          {/* Dismiss button - absolute on mobile, inline on desktop */}
          <button
            onClick={() => setDismissed(true)}
            className="absolute top-2 right-2 md:hidden p-1 text-muted-foreground hover:text-foreground transition-colors"
            aria-label="Dismiss"
          >
            <X className="h-4 w-4" />
          </button>

          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2 md:gap-4">
            <div className="flex items-center gap-3 flex-1 pr-6 md:pr-0">
              <div className="flex-shrink-0">
                <Sparkles className="h-5 w-5 text-primary" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-foreground">
                  You're using a guest account
                </p>
                <p className="text-xs text-muted-foreground">
                  Sign up to keep your notes permanently and sync across devices
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2 w-full md:w-auto flex-shrink-0">
              <button
                onClick={() => handleOAuthUpgrade('google')}
                disabled={loading}
                className="flex-1 md:flex-none px-3 py-1.5 text-xs font-medium bg-secondary text-secondary-foreground rounded-md hover:bg-secondary/80 transition-colors disabled:opacity-50"
              >
                <GoogleIcon className="inline h-3 w-3 mr-1" />
                Google
              </button>
              <button
                onClick={() => handleOAuthUpgrade('github')}
                disabled={loading}
                className="flex-1 md:flex-none px-3 py-1.5 text-xs font-medium bg-secondary text-secondary-foreground rounded-md hover:bg-secondary/80 transition-colors disabled:opacity-50"
              >
                <Github className="inline h-3 w-3 mr-1" />
                GitHub
              </button>
              <button
                onClick={() => setShowUpgradeModal(true)}
                disabled={loading}
                className="flex-1 md:flex-none px-3 py-1.5 text-xs font-medium bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors disabled:opacity-50"
              >
                <Mail className="inline h-3 w-3 mr-1" />
                Email
              </button>
              {/* Desktop dismiss button */}
              <button
                onClick={() => setDismissed(true)}
                className="hidden md:block p-1 text-muted-foreground hover:text-foreground transition-colors"
                aria-label="Dismiss"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>

          {error && (
            <div className="mt-2 p-2 bg-destructive/10 border border-destructive/20 rounded-md">
              <p className="text-xs text-destructive flex items-center gap-1">
                <AlertCircle className="h-3 w-3" />
                {error}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Upgrade Modal */}
      <AuthModal
        mode="upgrade"
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
      />
    </>
  );
}

